import { useState } from 'react';
import { toast } from 'react-toastify';
import {
    FaDesktop,
    FaUsers,
    FaNetworkWired,
    FaBroom,
    FaLock,
    FaWrench,
    FaCode,
    FaTerminal,
    FaTimes,
    FaCog,
    FaSync,
    FaPlay,
    FaPowerOff,
    FaExclamationTriangle,
    FaCheckCircle,
    FaTimesCircle
} from 'react-icons/fa';
import * as Sentry from '@sentry/react';

const ScriptRunnerModal = ({ isOpen, onClose, device, templates, categories, apiFetch }) => {
    const [activeCategory, setActiveCategory] = useState('Diagnostics');
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [params, setParams] = useState({});
    const [customBody, setCustomBody] = useState('');
    const [running, setRunning] = useState(false);
    const [result, setResult] = useState(null);

    const getCategoryIcon = (cat) => {
        const icons = {
            Diagnostics: <FaDesktop />, 'User Support': <FaUsers />, Network: <FaNetworkWired />,
            Maintenance: <FaBroom />, Security: <FaLock />, Repair: <FaWrench />, Custom: <FaCode />
        };
        return icons[cat] || <FaTerminal />;
    };

    const getRiskColor = (risk) => {
        if (risk === 'High') return 'uem-script-risk--high';
        if (risk === 'Medium') return 'uem-script-risk--medium';
        return 'uem-script-risk--low';
    };

    const handleSelectTemplate = (template) => {
        setSelectedTemplate(template);
        setResult(null);
        const defaults = {};
        (template.parameters || []).forEach(p => { defaults[p.name] = p.defaultValue || ''; });
        setParams(defaults);
        if (template.id === 'custom') setCustomBody('# Enter your PowerShell script here\nGet-ComputerInfo | Select-Object CsName, WindowsVersion');
    };

    const handleRunScript = async () => {
        if (!selectedTemplate || !device) return;
        setRunning(true);
        setResult(null);
        try {
            const body = {
                deviceId: device.deviceId,
                deviceName: device.deviceName,
                templateId: selectedTemplate.id,
                parameters: params
            };
            if (selectedTemplate.id === 'custom') body.customScriptBody = customBody;

            const res = await apiFetch('/api/UnifiedEndpoint/scripts/run', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body)
            });
            const data = await res.json();
            setResult(data);
            if (data.success) toast.success('Script deployed successfully.');
            else toast.error(data.message || 'Script deployment failed.');
        } catch (err) {
            Sentry.captureException(err);
            toast.error('Script execution failed.');
            setResult({ success: false, message: 'Request failed.' });
        } finally {
            setRunning(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="uem-modal-overlay" onClick={onClose}>
            <div className="uem-script-modal" onClick={e => e.stopPropagation()}>
                <div className="uem-script-modal-header">
                    <h2><FaTerminal /> Script Runner</h2>
                    <span className="uem-script-device-name">
                        <FaDesktop /> {device?.deviceName}
                    </span>
                    <button type="button" className="uem-modal-close" onClick={onClose} aria-label="Close"><FaTimes /></button>
                </div>

                <div className="uem-script-modal-body">
                    {/* Category Tabs */}
                    <div className="uem-script-categories">
                        {categories.map(cat => (
                            <button
                                type="button"
                                key={cat}
                                className={`uem-script-cat-tab ${activeCategory === cat ? 'uem-script-cat-tab--active' : ''}`}
                                onClick={() => { setActiveCategory(cat); setSelectedTemplate(null); setResult(null); }}
                            >
                                {getCategoryIcon(cat)} {cat}
                            </button>
                        ))}
                    </div>

                    <div className="uem-script-content">
                        {/* Template List */}
                        <div className="uem-script-list">
                            {templates
                                .filter(t => t.category === activeCategory)
                                .map(t => (
                                    <div
                                        key={t.id}
                                        className={`uem-script-card ${selectedTemplate?.id === t.id ? 'uem-script-card--selected' : ''}`}
                                        onClick={() => handleSelectTemplate(t)}
                                    >
                                        <div className="uem-script-card-header">
                                            <span className="uem-script-card-name">{t.name}</span>
                                            <span className={`uem-script-risk ${getRiskColor(t.riskLevel)}`}>{t.riskLevel}</span>
                                        </div>
                                        <p className="uem-script-card-desc">{t.description}</p>
                                        <div className="uem-script-card-meta">
                                            {t.requiresAdmin && <span className="uem-script-meta-tag"><FaLock /> Admin</span>}
                                            {t.requiresRestart && <span className="uem-script-meta-tag uem-script-meta-tag--warn"><FaPowerOff /> Restart</span>}
                                            <span className="uem-script-meta-tag"><FaCog /> ~{t.estimatedDurationSeconds}s</span>
                                        </div>
                                    </div>
                                ))}
                        </div>

                        {/* Script Detail / Run Panel */}
                        <div className="uem-script-detail">
                            {!selectedTemplate ? (
                                <div className="uem-script-placeholder">
                                    <FaTerminal className="uem-script-placeholder-icon" />
                                    <p>Select a script template from the list</p>
                                </div>
                            ) : (
                                <>
                                    <h3 className="uem-script-detail-title">{selectedTemplate.name}</h3>
                                    <p className="uem-script-detail-desc">{selectedTemplate.description}</p>

                                    {/* Parameters */}
                                    {selectedTemplate.parameters?.length > 0 && selectedTemplate.id !== 'custom' && (
                                        <div className="uem-script-params">
                                            <h4>Parameters</h4>
                                            {selectedTemplate.parameters.map(p => (
                                                <div key={p.name} className="uem-script-param">
                                                    <label>{p.displayName}</label>
                                                    <input
                                                        type="text"
                                                        value={params[p.name] || ''}
                                                        onChange={e => setParams(prev => ({ ...prev, [p.name]: e.target.value }))}
                                                        placeholder={p.description}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* Custom Script Editor */}
                                    {selectedTemplate.id === 'custom' && (
                                        <div className="uem-script-editor">
                                            <h4>PowerShell Script</h4>
                                            <textarea
                                                className="uem-script-textarea"
                                                value={customBody}
                                                onChange={e => setCustomBody(e.target.value)}
                                                rows={12}
                                                spellCheck={false}
                                            />
                                        </div>
                                    )}

                                    {/* Script Preview (non-custom) */}
                                    {selectedTemplate.id !== 'custom' && selectedTemplate.scriptBody && (
                                        <details className="uem-script-preview">
                                            <summary>View Script</summary>
                                            <pre>{selectedTemplate.scriptBody}</pre>
                                        </details>
                                    )}

                                    {/* Run Button */}
                                    <div className="uem-script-run-section">
                                        {selectedTemplate.riskLevel === 'High' && (
                                            <div className="uem-script-warning">
                                                <FaExclamationTriangle /> This is a high-risk script. Review carefully before running.
                                            </div>
                                        )}
                                        {selectedTemplate.requiresRestart && (
                                            <div className="uem-script-warning uem-script-warning--info">
                                                <FaPowerOff /> This script may require a device restart to take effect.
                                            </div>
                                        )}
                                        <button
                                            type="button"
                                            className="uem-script-run-btn"
                                            onClick={handleRunScript}
                                            disabled={running}
                                        >
                                            {running ? <><FaSync className="uem-spin" /> Deploying...</> : <><FaPlay /> Run on {device?.deviceName}</>}
                                        </button>
                                    </div>

                                    {/* Result */}
                                    {result && (
                                        <div className={`uem-script-result ${result.success ? 'uem-script-result--success' : 'uem-script-result--error'}`}>
                                            <div className="uem-script-result-main">
                                                {result.success ? <FaCheckCircle /> : <FaTimesCircle />}
                                                <span>{result.message}</span>
                                            </div>
                                            {result.success && (
                                                <div className="uem-script-result-meta">
                                                    {result.scriptId && (
                                                        <span className="uem-script-result-id">Script ID: {result.scriptId}</span>
                                                    )}
                                                    <span className={`uem-script-sync-badge ${result.syncTriggered ? 'uem-script-sync-badge--ok' : 'uem-script-sync-badge--warn'}`}>
                                                        <FaSync size={11} />
                                                        {result.syncTriggered ? 'Sync sent' : 'Sync not sent'}
                                                    </span>
                                                    {result.lastSyncDateTime && (
                                                        <span className="uem-script-result-id">
                                                            Last seen: {new Date(result.lastSyncDateTime).toLocaleString()}
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                            {!result.success && result.scriptId && (
                                                <span className="uem-script-result-id">Script ID: {result.scriptId}</span>
                                            )}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ScriptRunnerModal;
